import moment from "moment-timezone";

/**
 * Active display timezone for the live chat / app UI.
 *
 * The active timezone is a *display-only* concern. All database/API timestamps
 * are UTC. We convert at render time to the user's selected zone, falling back
 * to the browser's detected zone if nothing else is set.
 *
 * Source of truth at runtime: `TimezoneContext` (sets this via setActiveTimezone
 * after fetching the account preference). Callers may also pass an explicit
 * `tz` argument to override the active value (handy for tests).
 */
const FALLBACK_TIMEZONE = (() => {
  try {
    return moment.tz.guess() || "UTC";
  } catch (_e) {
    return "UTC";
  }
})();

let activeTimezone = FALLBACK_TIMEZONE;

/**
 * Set the global active display timezone. Silently ignores invalid values.
 * @param {string|null|undefined} tz - IANA timezone (e.g. "Asia/Kolkata")
 */
export const setActiveTimezone = (tz) => {
  if (!tz) {
    activeTimezone = FALLBACK_TIMEZONE;
    return;
  }
  if (typeof tz === "string" && moment.tz.zone(tz)) {
    activeTimezone = tz;
  }
};

/**
 * Get the currently-active display timezone (IANA identifier).
 */
export const getActiveTimezone = () => activeTimezone;

/**
 * Get the browser's detected fallback timezone (the value used when nothing
 * has been set explicitly). Useful when callers need to show a default.
 */
export const getFallbackTimezone = () => FALLBACK_TIMEZONE;

const resolveTimezone = (tz) =>
  tz && typeof tz === "string" && moment.tz.zone(tz) ? tz : activeTimezone;

/**
 * Parse a timestamp from database (UTC format) and return a moment object in UTC
 * Handles both ISO strings and MySQL datetime format (YYYY-MM-DD HH:MM:ss)
 *
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {moment.Moment|null} - Moment object in UTC, or null if invalid
 */
export const parseUTCTimestamp = (timestamp) => {
  if (!timestamp) return null;

  let momentObj = null;

  if (timestamp instanceof Date) {
    momentObj = moment.utc(timestamp);
  } else if (typeof timestamp === "number") {
    // If it's in seconds, convert to milliseconds
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    momentObj = moment.utc(ms);
  } else if (typeof timestamp === "string") {
    // MySQL datetime format (YYYY-MM-DD HH:MM:ss) — treat as UTC
    const mysqlFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (mysqlFormat.test(timestamp.trim())) {
      momentObj = moment.utc(timestamp, "YYYY-MM-DD HH:mm:ss");
    } else {
      momentObj = moment.utc(timestamp);
    }
  }

  if (!momentObj || !momentObj.isValid()) {
    return null;
  }

  return momentObj;
};

/**
 * Convert a UTC timestamp to the active (or supplied) timezone and format for
 * display. Shows relative time ("5m ago", "2h ago") for recent values and an
 * absolute date for older ones.
 *
 * Note: the "X ago" math is intentionally timezone-independent (real elapsed
 * time is the same regardless of zone). The zone only affects the absolute
 * fallback shown for timestamps older than 7 days.
 *
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @param {string} [tz] - Optional IANA timezone override
 * @returns {string} - Formatted string for display
 */
export const formatTimestampForDisplay = (timestamp, tz) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return "";

  const zone = resolveTimezone(tz);
  const localMoment = utcMoment.clone().tz(zone);
  const now = moment().tz(zone);

  const diffMinutes = Math.floor(now.diff(localMoment, "minutes", true));
  const diffHours = Math.floor(now.diff(localMoment, "hours", true));
  const diffDays = Math.floor(now.diff(localMoment, "days", true));

  if (diffMinutes < 0) return "Just now";
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return localMoment.format("MMM DD, YYYY");
};

/**
 * Format timestamp with date AND time in the active (or supplied) timezone.
 *
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @param {string} [format] - Moment format string (default: "MMM DD, YYYY hh:mm A")
 * @param {string} [tz] - Optional IANA timezone override
 * @returns {string}
 */
export const formatTimestampDetailed = (
  timestamp,
  format = "MMM DD, YYYY hh:mm A",
  tz
) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return "";
  return utcMoment.tz(resolveTimezone(tz)).format(format);
};

/**
 * Get timestamp value for sorting (returns UTC timestamp as number)
 *
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {number} - Timestamp in milliseconds (UTC), or 0 if invalid
 */
export const getTimestampForSorting = (timestamp) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return 0;
  return utcMoment.valueOf();
};

/**
 * Normalize timestamp to ISO string for consistent storage/comparison.
 *
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @returns {string|null}
 */
export const normalizeTimestampToISO = (timestamp) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return null;
  return utcMoment.toISOString();
};

/**
 * Format timestamp for HTML datetime-local input in the active (or supplied)
 * timezone.
 *
 * @param {string|Date|number|null} timestamp - Timestamp from database (UTC)
 * @param {string} [tz] - Optional IANA timezone override
 * @returns {string} - Formatted string (YYYY-MM-DDTHH:mm)
 */
export const formatTimestampForInput = (timestamp, tz) => {
  const utcMoment = parseUTCTimestamp(timestamp);
  if (!utcMoment) return "";
  return utcMoment.tz(resolveTimezone(tz)).format("YYYY-MM-DDTHH:mm");
};

/**
 * Convert a datetime-local string entered in the active (or supplied) timezone
 * back to a UTC ISO string suitable for storage.
 *
 * Kept as `convertISTToUTC` for backwards compatibility with existing imports,
 * but it now respects the active timezone rather than always treating input as
 * IST.
 *
 * @param {string} localDateTime - DateTime in YYYY-MM-DDTHH:mm format
 * @param {string} [tz] - Optional IANA timezone override
 * @returns {string|null}
 */
export const convertLocalToUTC = (localDateTime, tz) => {
  if (!localDateTime) return null;
  try {
    const zone = resolveTimezone(tz);
    const localMoment = moment.tz(localDateTime, "YYYY-MM-DDTHH:mm", zone);
    if (!localMoment.isValid()) return null;
    return localMoment.utc().toISOString();
  } catch (error) {
    console.error("Error converting local to UTC:", error);
    return null;
  }
};

// Backwards-compatible alias (older code imports `convertISTToUTC`).
export const convertISTToUTC = convertLocalToUTC;

/**
 * Current UTC timestamp in MySQL datetime format (YYYY-MM-DD HH:MM:SS).
 */
export const getCurrentUTCTimestamp = () => {
  return moment.utc().format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Legacy: previously rendered timestamps in UTC instead of IST. Kept for
 * backwards compatibility, but now delegates to `formatTimestampForDisplay`
 * so the sidebar and message bubbles render in the same active timezone.
 */
export const formatTimestampForDisplayUTC = (timestamp, tz) =>
  formatTimestampForDisplay(timestamp, tz);
